import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { step1Schema } from '@shared/lib/validators/registration-schema';
import { Controller, useForm } from 'react-hook-form';
import {
  selectCurrentStep,
  setStep,
} from '../../../entities/registration/model';
import { useEffect } from 'react';
import { formatPhoneNumber } from '@shared/lib/validators/phone-normalize';
import { useFieldArray } from 'react-hook-form';
import { Button } from '../../../shared/ui/button';
import { InputField } from '../../../shared/ui/input-field';
import redPlusCircle from '../../../shared/assets/sprites/red_plus_circle.svg';
import ReactInputMask from 'react-input-mask';
import logo from '../../../shared/assets/images/register-logo.png';
import back from '../../../shared/assets/sprites/arrow_back.png';
import {
  addCompanyPartialData,
  selectLegalEntities,
} from '../../../entities/registration/model';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { getTaxLabelForLegalEntity } from '@shared/lib/tax-label';

export const RegisterLeftSide = () => {
  return (
    <div>
      <section className="flex justify-center items-center flex-col absolute top-[45%] left-[25%] -translate-x-1/2 -translate-y-[20%] text-center w-[346px]">
        <img src={logo} alt="" />
      </section>
    </div>
  );
};

interface IRegisterHeader {
  title: string;
  description?: string;
  /**
   * Номер текущего шага (1..5). Если не передан или 0 — счётчик
   * не показывается. Раньше тип был обязательным, и
   * `RegistrationStepConfirmation` падал в TS, потому что step
   * там не нужен.
   */
  step?: number;
  handler?: () => void;
  isBack?: boolean;
  stepSize?: number;
}

export const RegisterHeader = ({
  title,
  description,
  step,
  isBack,
  stepSize = 1,
}: IRegisterHeader) => {
  const currentStep = useSelector(selectCurrentStep);
  const dispatch = useAppDispatch();
  const handleBack = () => {
    dispatch(setStep(stepSize));
  };

  // Раньше «Назад» и «N/5» позиционировались абсолютно поверх
  // блока с заголовком — при длинных описаниях текст
  // накладывался друг на друга (см. скриншот «Данные компании»).
  // Теперь это обычный flex-ряд НАД заголовком: занимает свою
  // высоту, ничего не перекрывает, на узких экранах просто
  // переносится по ширине.
  const showBack = currentStep !== 0 && isBack;
  const showCounter = typeof step === 'number' && step > 0;

  return (
    <div className="w-full max-w-[70%]">
      {(showBack || showCounter) && (
        <div className="flex items-center justify-between mb-4 min-h-[24px]">
          {showBack ? (
            <button
              type="button"
              onClick={handleBack}
              className="bg-transparent border-none flex items-center font-medium text-gray-500 hover:text-[#e3282a] transition-colors"
            >
              <img src={back} alt="" />
              <span className="ml-[10px]">Назад</span>
            </button>
          ) : (
            // Пустой spacer, чтобы счётчик уехал в правый край
            // даже когда «Назад» не отображается.
            <span aria-hidden />
          )}
          {showCounter && (
            <div className="text-[#e3282a] font-medium">{step}/5</div>
          )}
        </div>
      )}
      <div className="text-[#141414] text-[28px] font-bold leading-tight">
        {title}
      </div>
      {description && (
        <p className="text-[#b4b4b4] text-[18px] leading-[25px] mt-2">
          {description}
        </p>
      )}
    </div>
  );
};

export const RegistrationStepCompanyData = () => {
  const newLegalEntities = useAppSelector(selectLegalEntities);
  const dispatch = useAppDispatch();

  // Лейбл налогового номера зависит от страны юр.лица. Источник —
  // адрес из партнёрского инвайта 1С (см. addCompanyData reducer:
  // codeAlfa3='KAZ' → addresses[].country='kz'). Один общий хелпер
  // в shared/lib/tax-label, чтобы все экраны показывали одно и то же.
  const taxNumberLabel = getTaxLabelForLegalEntity(newLegalEntities?.[0]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(step1Schema),
    shouldUnregister: false,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'phones',
  });

  useEffect(() => {
    reset({
      name: newLegalEntities?.[0]?.name,
      inn: newLegalEntities?.[0]?.inn,
      email: newLegalEntities?.[0]?.email,
      online_documents: newLegalEntities?.[0]?.online_documents || false,
      phones:
        newLegalEntities?.[0]?.phones?.map((phone) => ({
          phone: formatPhoneNumber(phone),
        })).length > 0
          ? newLegalEntities?.[0]?.phones?.map((phone) => ({
              phone: formatPhoneNumber(phone),
            }))
          : [{ phone: '' }],
    });
  }, [newLegalEntities, reset]);

  const onSubmit = (data: Record<string, unknown>) => {
    const phones = (data.phones as { phone: string }[]).map((phone) => phone.phone);
    if (data.phoneInput) phones.push(data.phoneInput as string);
    const { phoneInput, ...rest } = data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(addCompanyPartialData({ ...rest, phones } as any));
  };

  return (
    <>
      <RegisterHeader
        step={1}
        isBack={true}
        stepSize={0}
        title="Данные компании"
        description="Заполните регистрационные данные. Это займет пару минут."
      />
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <InputField inputName="name" id="name" label="Имя" register={register} errors={errors} />
        <InputField inputName="inn" id="inn" label={taxNumberLabel} register={register} errors={errors} />
        <InputField inputName="email" id="email" label="Email" register={register} errors={errors} />
        <div className="flex items-center justify-between w-full">
          <div className="flex flex-col -mt-[26px] w-full">
            <div className="flex mt-[10px]">
              <p>Номера телефонов</p>
            </div>
            {fields.map((item, index) => (
              <div key={item.id}>
                <div className="flex mt-[10px]">
                  <p className="text-right text-[#e23235] text-sm font-medium leading-5 mb-2">
                    {errors?.phones?.root?.message}
                  </p>
                  <p className="text-right text-[#e23235] text-sm font-medium leading-5 mb-2">
                    {errors?.phones?.message}
                  </p>
                </div>
                <div className="mt-5 h-[42px] flex justify-between items-center w-full [&>div]:flex-1">
                  <Controller
                    name={`phones.${index}.phone`}
                    control={control}
                    defaultValue={item.phone}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <ReactInputMask
                        mask="+7 (999) 999-99-99"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        id={`phone-${index}`}
                      >
                        <InputField
                          inputName={`phones[${index}].phone`}
                          ref={ref}
                          errors={errors?.phones?.[index]?.phone?.message}
                          deleteIconHandler={
                            index > 0
                              ? (e) => {
                                  e.preventDefault();
                                  remove(index);
                                }
                              : undefined
                          }
                        />
                      </ReactInputMask>
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        {fields.length >= 3 ? null : (
          <div
            className="flex cursor-pointer justify-center items-center mt-[10px] w-fit"
            onClick={(e) => {
              e.preventDefault();
              append({ phone: '' });
            }}
          >
            <img src={redPlusCircle} alt="Add phone" className="-mt-[2px] -ml-[2px]" />
            <span className="ml-[10px] text-base font-medium">Добавить номер</span>
          </div>
        )}
        <div className="flex my-5 items-center">
          <input
            className="bg-[#9e9e9e] accent-[#e3282a] h-5 w-5 align-sub cursor-pointer"
            type="checkbox"
            id="online_documents"
            {...register('online_documents')}
          />
          <p className="ml-[10px]">Отправить документы онлайн</p>
        </div>
        <Button type="submit">Продолжить</Button>
      </form>
    </>
  );
};
